import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    Bell,
    User,
    ChevronDown,
    Shield,
    Menu,
    X,
    BarChart2,
    Users,
    Building2,
    Globe,
    ShoppingBag,
    Wallet,
    MessageSquare,
    Settings,
    BadgeCheck,
    HelpCircle,
    Clock,
    FileText,
    DollarSign,
    ArrowUpRight,
    AlertCircle,
    AlertTriangle,
    Star,
    BookOpen,
    ArrowUp,
    MapPin,
    TowerControlIcon,
    MessageCircle,
    WrenchIcon,
    CreditCard,
    PieChart,
    Database,
    FormInput,
    BookText,
    Newspaper,
    BellRing,
    LayoutDashboard,
    Languages,
    DollarSign as Currency,
    Settings2,
    Sliders,
    Megaphone,
    Flag,
    Send,
    CheckSquare,
    UserCog,
    MessageCircleIcon,
    MessageSquareIcon,
    Lock,
    TrendingUp,
    Award,
} from "lucide-react";
import { LiaWhatsapp } from "react-icons/lia";
import { getAllUserCounts, LogoutManually, LogoutUser, useAppDispatch } from "../redux";
import { useSelector } from "react-redux";

const AdminHeader = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const location = useLocation();
    const activeItemRef = useRef(null);
    const desktopSidebarRef = useRef(null);
    const mobileSidebarRef = useRef(null);
    const { userCounts } = useSelector((state: any) => state.admin);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
            navigate("/auth/login");
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
            } else {
            }
        }
    };
    // Function to scroll to active item
    const scrollToActiveItem = sidebarRef => {
        if (activeItemRef.current && sidebarRef.current) {
            const sidebar = sidebarRef.current;
            const activeItem = activeItemRef.current;

            // Calculate the scroll position
            const itemOffset = activeItem.offsetTop;
            const sidebarHeight = sidebar.clientHeight;
            const itemHeight = activeItem.clientHeight;

            // Center the item in the viewport
            const scrollPosition = itemOffset - sidebarHeight / 2 + itemHeight / 2;

            // Ensure we don"t scroll past the top
            sidebar.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: "smooth",
            });
        }
    };
    const getUserCounts = async () => {
        await dispatch(getAllUserCounts());
    };
    useEffect(() => {
        getUserCounts();
    }, []);

    // Handle scroll for both desktop and mobile when location changes
    useEffect(() => {
        // Scroll desktop sidebar
        if (desktopSidebarRef.current) {
            scrollToActiveItem(desktopSidebarRef);
        }

        // Scroll mobile sidebar if it"s open
        if (mobileSidebarRef.current && isMobileMenuOpen) {
            scrollToActiveItem(mobileSidebarRef);
        }
    }, [location.pathname, isMobileMenuOpen]);

    // Scroll mobile sidebar when it"s opened
    useEffect(() => {
        if (isMobileMenuOpen && mobileSidebarRef.current) {
            scrollToActiveItem(mobileSidebarRef);
        }
    }, [isMobileMenuOpen]);

    const menuItems = [
        {
            heading: "OVERVIEW",
            items: [{ icon: BarChart2, label: "Dashboard", href: "/admin/dashboard" }],
        },
        {
            heading: "USER MANAGEMENT",
            items: [
                {
                    icon: Users,
                    label: "All Users",
                    href: "/admin/usermanagement/alluser",
                    badge: userCounts != null ? userCounts?.totalUsers : "0",
                },
                {
                    icon: Building2,
                    label: "Local Professionals",
                    href: "/admin/usermanagement/localprofesionals",
                    badge: userCounts != null ? userCounts?.professionals : "0",
                },
                {
                    icon: Globe,
                    label: "International Professionals",
                    href: "/admin/usermanagement/internationalprofesionals",
                    badge: userCounts != null ? userCounts?.internationalProfessionals : "0",
                },
                {
                    icon: BadgeCheck,
                    label: "Verification Requests",
                    href: "/admin/usermanagement/verificationrequests",
                },
            ],
        },
        {
            heading: "BUSINESS MANAGEMENT",
            items: [
                { icon: Building2, label: "Business Directory", href: "/admin/businesses" },
                { icon: TowerControlIcon, label: "Service Categories", href: "/admin/categories" },
                { icon: AlertCircle, label: "Emergency Services", href: "/admin/emergency-services" },
                { icon: MapPin, label: "Service Areas", href: "/admin/service-areas" },
            ],
        },
        {
            heading: "ORDER MANAGEMENT",
            items: [
                { icon: ShoppingBag, label: "All Orders", href: "/admin/orders" },
                { icon: Clock, label: "Pending Orders", href: "/admin/orders/pending" },
                { icon: AlertCircle, label: "Emergency Requests", href: "/admin/orders/emergency" },
                { icon: FileText, label: "Quotes", href: "/admin/quotes" },
                { icon: AlertTriangle, label: "Dispute Requests", href: "/admin/dispute-requests" },
            ],
        },
        {
            heading: "FINANCIAL MANAGEMENT",
            items: [
                { icon: Wallet, label: "Transactions", href: "/admin/transactions" },
                { icon: DollarSign, label: "Revenue", href: "/admin/revenue" },
                { icon: ArrowUpRight, label: "Payouts", href: "/admin/payouts" },
                { icon: FileText, label: "Invoices", href: "/admin/invoices" },
            ],
        },
        {
            heading: "CONTENT MANAGEMENT",
            items: [
                { icon: Star, label: "Reviews & Ratings", href: "/admin/reviews" },
                { icon: MessageSquare, label: "Support Tickets", href: "/admin/support" },
                { icon: HelpCircle, label: "Help Center", href: "/admin/help-center" },
                { icon: Bell, label: "Announcements", href: "/admin/announcements" },
            ],
        },
        {
            heading: "PROFESSIONAL SCORING",
            items: [
                { icon: TrendingUp, label: "Performance Metrics", href: "/admin/scoring/metrics" },
                { icon: Award, label: "Rankings", href: "/admin/scoring/rankings" },
                { icon: Settings, label: "Scoring Settings", href: "/admin/scoring/settings" },
                { icon: Shield, label: "Monitoring & Reports", href: "/admin/scoring/monitoring" },
            ],
        },
        {
            heading: "ANALYTICS",
            items: [
                { icon: BarChart2, label: "Business Analytics", href: "/admin/analytics/business" },
                { icon: Users, label: "User Analytics", href: "/admin/analytics/users" },
                { icon: ArrowUp, label: "Growth Metrics", href: "/admin/analytics/growth" },
                { icon: ArrowUp, label: "Chat Management", href: "/admin/analytics/chat" },
            ],
        },
        {
            heading: "SETTINGS",
            items: [
                { icon: Lock, label: "Change Password", href: "/changepassword" },
                { icon: Settings, label: "General Settings", href: "/admin/settings" },
                { icon: Shield, label: "Security", href: "/admin/security" },
                { icon: BookOpen, label: "Logs", href: "/admin/logs" },
                { icon: Star, label: "Trustpilot Reviews", href: "/admin/trustpilot" },
            ],
        },
        {
            heading: "COMMUNICATION TOOLS",
            items: [
                { icon: MessageSquareIcon, label: "WhatsApp Integration", href: "/admin/tools/whatsapp" },
                { icon: MessageSquare, label: "Chat Management", href: "/admin/tools/chat" },
                { icon: BellRing, label: "Custom Notifications", href: "/admin/tools/notifications" },
                { icon: BellRing, label: "Favorites", href: "/admin/favorites" },
            ],
        },
        {
            heading: "FORM MANAGEMENT",
            items: [
                { icon: FormInput, label: "Category Forms", href: "/admin/forms/categories" },
                // { icon: UserCog, label: "Professional Forms", href: "/admin/forms/professionals" },
                { icon: FileText, label: "Quote Forms", href: "/admin/forms/quotes" },
                { icon: FileText, label: "Location Management", href: "/locationmanagement" },
            ],
        },
        {
            heading: "SUBSCRIPTION & PRICING",
            items: [
                { icon: CreditCard, label: "Quote Pricing", href: "/admin/pricing/quotes" },
                { icon: Settings2, label: "Subscription Plans", href: "/admin/pricing/subscriptions" },
                { icon: MapPin, label: "Regional Pricing", href: "/admin/pricing/regions" },
            ],
        },
        {
            heading: "MILESTONE MANAGEMENT",
            items: [
                { icon: CheckSquare, label: "Payment Milestones", href: "/admin/milestones/payments" },
                { icon: Sliders, label: "Commission Settings", href: "/admin/milestones/commission" },
                { icon: Shield, label: "Deposit Rules", href: "/admin/milestones/deposits" },
            ],
        },
        {
            heading: "CONTENT EDITOR",
            items: [
                { icon: FileText, label: "Terms & Conditions", href: "/admin/editor/terms" },
                { icon: MessageSquare, label: "Marketing Popups", href: "/admin/editor/marketing" },
                // { icon: MessageSquare, label: "Edit Client Profile", href: "/admin/editprofile" },
            ],
        },
        {
            heading: "INTERNATIONAL",
            items: [
                { icon: Globe, label: "Region Settings", href: "/admin/international/regions" },
                { icon: Languages, label: "Language Manager", href: "/admin/international/languages" },
                { icon: Currency, label: "Currency Settings", href: "/admin/international/currencies" },
            ],
        },
    ];

    const NavItem = ({ item, onClick }) => {
        const isActive = location.pathname === item.href;

        return (
            <Link
                ref={isActive ? activeItemRef : null}
                to={item.href}
                className="flex items-center justify-between px-4 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded"
                onClick={onClick}
                style={{ textAlign: "left" }}>
                <div className="flex items-center gap-3">
                    <item.icon className={`h-5 w-5 ${isActive ? "text-blue-600" : "text-gray-500"}`} />
                    <span className={isActive ? "text-blue-600" : ""}>{item.label}</span>
                </div>
                {item.badge && <span className="text-blue-600 text-sm">{item.badge}</span>}
            </Link>
        );
    };

    return (
        <>
            <header className="bg-white border-b fixed top-0 inset-x-0 z-40 h-16" style={{ textAlign: "left" }}>
                <div className="flex items-center justify-between h-full px-4">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2">
                            <Menu className="h-6 w-6" />
                        </button>
                        
                        {/* User Menu */}
                        <div className="relative">
                            <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2">
                                <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <User className="h-5 w-5" />
                                </div>
                                <span className="font-medium">Admin</span>
                                <ChevronDown className="h-4 w-4" />
                            </button>

                            {showUserMenu && (
                                <div className="absolute left-0 mt-2 w-48 bg-white rounded shadow-lg border">
                                    <div className="p-4 border-b">
                                        <p className="font-medium">Admin Account</p>
                                        <p className="text-sm text-gray-500">admin@kokobeo.com</p>
                                    </div>
                                    <Link to="/admin/settings" className="block px-4 py-2 hover:bg-gray-50">
                                        Settings
                                    </Link>
                                    <Link to="/admin/security" className="block px-4 py-2 hover:bg-gray-50">
                                        Security
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-50 border-t">
                                        Log Out
                                    </button>
                                </div>
                            )}
                        </div>

                        <Link to="/admin/dashboard" className="flex items-center gap-2">
                            <Shield className="h-8 w-8 text-blue-600" />
                            <span className="text-xl font-bold text-blue-600">Kokobeo</span>
                        </Link>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Additional controls can go here if needed */}
                    </div>
                </div>
            </header>

            {/* Desktop Sidebar */}
            <aside
                ref={desktopSidebarRef}
                className="fixed left-0 top-16 bottom-0 w-64 bg-white border-r hidden lg:block overflow-y-auto">
                <nav className="py-4 mb-16">
                    {menuItems.map((section, index) => (
                        <div key={index} className="mb-6">
                            <h3 className="px-4 text-xs font-medium text-gray-500 mb-2">{section.heading}</h3>
                            <div className="space-y-1">
                                {section.items.map((item, itemIndex) => (
                                    <NavItem onClick={() => {}} key={itemIndex} item={item} />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
            </aside>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden" style={{ textAlign: "left" }}>
                    <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
                    <aside
                        ref={mobileSidebarRef}
                        className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b">
                            <div className="flex items-center gap-2">
                                <Shield className="h-8 w-8 text-blue-600" />
                                <span className="font-bold text-xl text-blue-600">Kokobeo</span>
                            </div>
                            <button onClick={() => setIsMobileMenuOpen(false)}>
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <nav className="p-4 pb-20">
                            {menuItems.map((section, index) => (
                                <div key={index} className="mb-6">
                                    <h3 className="px-4 text-xs font-medium text-gray-500 mb-2">{section.heading}</h3>
                                    <div className="space-y-1">
                                        {section.items.map((item, itemIndex) => (
                                            <NavItem
                                                key={itemIndex}
                                                item={item}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </nav>
                    </aside>
                </div>
            )}
        </>
    );
};

export default AdminHeader;
